import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { ensureFreshToken } from '@/utils/api';
import {
  ACCOUNT_MANAGE,
  DATA_BACKUP,
  INVENTORY_ADJUST,
  ORDER_VOID,
  PRICE_OVERRIDE,
  REFUND_CREATE,
  REPORT_VIEW,
} from '@/utils/permissions';

/**
 * v8.3.0 P3-08（T23）：路由鉴权从「数字角色」升级为「权限点」。
 *
 * 三条口径必须先说清楚，否则很容易改出一个「能编译但不解决问题」的版本：
 *
 * 1) 【只挡受控动作，不挡工作台】meta.permission 只挂在 v3.0 §2.5 的 7 个受控动作上。
 *    收银员日常必经的路由（收银台 /pos、商品库查看 /products、库存只读 /inventory、
 *    盘点执行 /stock-check 与 /stock-check-task/:id、本班次交班 /shift/current）一律
 *    不加判据——它们不是受控动作，服务端那边也没有挂权限点（例如
 *    stock-check.controller.ts:71 的 tasks/:id/start、:76 的 submit 都没有 @Roles）。
 *    基线这几条是用 meta.roles 挡的，本项按口径放开给收银员；真正该收的是
 *    「盘点差异调整」（服务端 :89 adjust 已挂 inventory.adjust），那一条在后端生效。
 *
 * 2) 【判据只有一个来源：服务端下发的 permissions 数组】
 *    这里不做 role → 权限的第二次推导（那是第二份事实源，必然与后端分叉）。
 *    判定走 usePermission 的同一套语义：缺失 / 空数组 / 未知权限点 → false（fail-closed）。
 *
 * 3) 【老缓存要先补权限再判】
 *    升级前登录过的账号 localStorage.user 里没有 permissions。如果刷新后立刻判，
 *    店主的权限集还是空的 → 手输 /report 会被判成无权限，比原来的静默跳首页更难查。
 *    所以未解析时先 await auth.ensurePermissions()（store 内单飞去重，不会重复打接口），
 *    拿到真值再判；仍然失败才按 fail-closed 拒。
 */

/** 权限点 → 中文名（验收 2.5.5 要求提示可读，页面绝不能显示 price.override 这类编码） */
export const PERMISSION_LABELS: Record<string, string> = {
  [PRICE_OVERRIDE]: '改价与折扣',
  [REFUND_CREATE]: '退款',
  [ORDER_VOID]: '删单与作废',
  [INVENTORY_ADJUST]: '库存调整',
  [REPORT_VIEW]: '报表与成本',
  [ACCOUNT_MANAGE]: '账号与权限管理',
  [DATA_BACKUP]: '数据备份与恢复',
};

/**
 * 路由表。
 * meta.permission  —— 需要的权限点（字符串，与后端编码一字不差）
 * meta.label       —— 该路由在无权限页上显示的中文名；缺省取权限点中文名。
 *                     个别路由与权限点不是一一对应（例如 /shifts 归到「经营数据」下的
 *                     report.view），就在这一层给一个更贴切的路由级中文名。
 */
const routes = [
  { path: '/', redirect: '/login' },
  { path: '/login', component: () => import('@/views/Login.vue'), meta: { public: true } },
  { path: '/home', component: () => import('@/views/Home.vue') },
  // 无权限提示页：本身不挂权限点，否则无权限的人连提示都看不到
  { path: '/no-permission', component: () => import('@/views/NoPermission.vue') },
  // 收银台：所有角色可用（非受控能力）
  { path: '/pos', component: () => import('@/views/POS.vue') },
  // 商品库查看：只读，所有人可进（成本价展示由页面内 can('report.view') 收，见 §3.3 #15）
  { path: '/products', component: () => import('@/views/Products.vue') },
  // 商品维护（新建/编辑）属「库存调整」口径：店长仍在用（v3.0 受控动作不含商品维护，不得平白收走）
  { path: '/product/new', component: () => import('@/views/ProductForm.vue'), meta: { permission: INVENTORY_ADJUST, label: '商品维护' } },
  { path: '/product/edit/:id', component: () => import('@/views/ProductForm.vue'), meta: { permission: INVENTORY_ADJUST, label: '商品维护' } },
  { path: '/product/:id', component: () => import('@/views/ProductDetail.vue') },
  // 库存查看：只读，所有人可进（入库/出库/报损三个按钮由页面内 can('inventory.adjust') 收）
  { path: '/inventory', component: () => import('@/views/Inventory.vue') },
  { path: '/stock-in', component: () => import('@/views/StockIn.vue'), meta: { permission: INVENTORY_ADJUST, label: '入库' } },
  { path: '/stock-out', component: () => import('@/views/StockOut.vue'), meta: { permission: INVENTORY_ADJUST, label: '出库与报损' } },
  // 盘点：执行与查看属日常动作，收银员必经，不加判据；
  // 差异调整（stock-check.controller.ts:89）与盘点计划（:27/:34/:41/:48）在后端各自的权限点生效。
  { path: '/stock-check', component: () => import('@/views/StockCheck.vue') },
  { path: '/stock-check-task/:id', component: () => import('@/views/StockCheckTask.vue') },
  { path: '/stock-check-report/:id', component: () => import('@/views/StockCheckReport.vue') },
  { path: '/stock-check-plan-config', component: () => import('@/views/StockCheckPlanConfig.vue'), meta: { permission: INVENTORY_ADJUST, label: '盘点计划' } },
  // 收银流水：收银员可看（服务端按「仅本人」收窄），不是受控动作
  { path: '/orders', component: () => import('@/views/Orders.vue') },
  { path: '/order/:id', component: () => import('@/views/OrderDetail.vue') },
  { path: '/scan-recognize', component: () => import('@/views/ScanRecognize.vue') },
  // 门店管理（建店/改店/删店）店主专属，复用 account.manage（店长没有这个权限点）
  { path: '/stores', component: () => import('@/views/Stores.vue'), meta: { permission: ACCOUNT_MANAGE, label: '门店管理' } },
  { path: '/profile', component: () => import('@/views/Profile.vue') },
  // 员工管理页（v8.3.0 T10b 修正点 1/2）：
  // 本页不挂 meta.permission，原因是定稿 §2.3 把账号管理按【字段级】收口——
  // 本页同时承载「建号/改角色/改资损开关」（account.manage，仅店主）与
  // 「重置密码/启停/改姓名」（矩阵明确保留给店长的日常动作，店长没有 account.manage）。
  // 若路由只认 account.manage，店长连页面都进不去 → 保留的那三项变成空话（等于缩小能力）；
  // 若路由放宽到「任何人可进」，收银员会看到只有自己的列表（矩阵允许「仅本人」，但无意义）。
  // 所以路由层不设判据，改为页内精确收口：受控动作一律 can('account.manage') 显隐，
  // 接口侧由 store.service 的字段级守卫兜底。7 个权限点里没有「员工页访问」这个点，
  // 硬塞一个代理权限点（如 inventory.adjust）会造成「矩阵与路由耦合」的隐性故障，故不采用。
  {
    path: '/staff',
    component: () => import('@/views/StaffManagement.vue'),
    meta: { label: '账号与员工管理' },
  },
  // 分类管理属商品维护口径（基线 product.controller.ts 的 @Roles(ADMIN, MANAGER)）
  { path: '/categories', component: () => import('@/views/CategoryManagement.vue'), meta: { permission: INVENTORY_ADJUST, label: '分类管理' } },
  // 报表：v3.0 §2.5 明确归「报表与成本」，店长可见（T09a 已放开后端两层 403）
  { path: '/report', component: () => import('@/views/Report.vue'), meta: { permission: REPORT_VIEW } },
  // 退货：新建与列表都归 refund.create（收银员一律挡在门外，与后端 refund 端点同口径）
  { path: '/refunds', component: () => import('@/views/RefundList.vue'), meta: { permission: REFUND_CREATE } },
  { path: '/refund/new', component: () => import('@/views/RefundCreate.vue'), meta: { permission: REFUND_CREATE } },
  // 交班列表归「经营数据」（report.view）；本班次交班 /shift/current 不受控，收银员必须能进
  { path: '/shifts', component: () => import('@/views/ShiftList.vue'), meta: { permission: REPORT_VIEW, label: '交班记录' } },
  { path: '/shift/current', component: () => import('@/views/ShiftCurrent.vue') },
  // 供应商 / 采购 / 调拨：基线与「入库出库」同一角色范围，统一走库存调整
  { path: '/suppliers', component: () => import('@/views/Suppliers.vue'), meta: { permission: INVENTORY_ADJUST, label: '供应商管理' } },
  { path: '/supplier/new', component: () => import('@/views/SupplierForm.vue'), meta: { permission: INVENTORY_ADJUST, label: '供应商管理' } },
  { path: '/supplier/edit/:id', component: () => import('@/views/SupplierForm.vue'), meta: { permission: INVENTORY_ADJUST, label: '供应商管理' } },
  { path: '/purchase-orders', component: () => import('@/views/PurchaseOrders.vue'), meta: { permission: INVENTORY_ADJUST, label: '采购管理' } },
  { path: '/purchase-order/new', component: () => import('@/views/PurchaseOrderForm.vue'), meta: { permission: INVENTORY_ADJUST, label: '采购管理' } },
  { path: '/stock-transfer-out', component: () => import('@/views/StockTransferOut.vue'), meta: { permission: INVENTORY_ADJUST, label: '调拨出库' } },
  { path: '/transfer-orders', component: () => import('@/views/TransferOrders.vue'), meta: { permission: INVENTORY_ADJUST, label: '调拨管理' } },
  { path: '/stock-transfer-in/:id', component: () => import('@/views/StockTransferIn.vue'), meta: { permission: INVENTORY_ADJUST, label: '调拨入库' } },
  // 促销维护改的是售价，归 price.override（店长可、收银员不可，与基线角色范围一致）
  { path: '/promotions', component: () => import('@/views/Promotions.vue'), meta: { permission: PRICE_OVERRIDE, label: '促销活动' } },
  { path: '/promotion/new', component: () => import('@/views/PromotionForm.vue'), meta: { permission: PRICE_OVERRIDE, label: '促销活动' } },
  { path: '/promotion/edit/:id', component: () => import('@/views/PromotionForm.vue'), meta: { permission: PRICE_OVERRIDE, label: '促销活动' } },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore();
  if (!to.meta.public && !auth.token) {
    next('/login');
    return;
  }
  // P2-5：进入页面前预检 token 是否已过期；过期则先静默刷新，
  // 刷新失败才跳登录（带 redirect 回跳），避免页面渲染后首个请求才 401
  if (!to.meta.public && auth.token && !(await ensureFreshToken())) {
    next('/login?redirect=' + encodeURIComponent(to.fullPath));
    return;
  }

  const required = to.meta.permission as string | undefined;
  if (!required || to.meta.public) {
    next();
    return;
  }

  // 老缓存 / 刚登录但响应里没有 permissions：先回读真值，避免把店主误判成无权限
  if (!auth.permissionsResolved) {
    await auth.ensurePermissions();
  }

  const perms = auth.permissions;
  const allowed = Array.isArray(perms) && perms.includes(required);
  if (!allowed) {
    // 基线在这里是 next('/home') —— 用户只看到自己被弹回首页，不知道为什么。
    // 现在带原路径与所缺权限点跳提示页，页面文案固定为可读中文（验收 2.5.5）。
    next({
      path: '/no-permission',
      query: {
        from: to.fullPath,
        perm: required,
        label: (to.meta.label as string) || PERMISSION_LABELS[required] || '',
      },
    });
    return;
  }
  next();
});

export default router;
